import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { take } from 'rxjs';
import { StudentNav } from '../../student-nav/student-nav';
import { Deletion } from './deletion/deletion';
import { UserService } from '../../../../services/user.service';
import { StudentPostsService } from '../../../../services/student-posts.service';
import {
  EmailCategory,
  EmailNotificationsService,
  EmailSettings,
  EmailSettingsUpdate,
} from '../../../../services/email-notifications.service';
import { Post } from '../../../../models/post';

/** Settings page for the student view. Sets the page title and exposes the current user to the template. */
@Component({
  selector: 'app-settings',
  imports: [
    CommonModule,
    StudentNav,
    Deletion,
    MatCardModule,
    MatButtonModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
  ],
  templateUrl: './settings.html',
  styleUrl: './settings.css',
})
export class Settings implements OnInit {
  private title = inject(Title);
  private userService = inject(UserService);
  private studentPostsService = inject(StudentPostsService);
  private emailNotificationsService = inject(EmailNotificationsService);
  private cdr = inject(ChangeDetectorRef);
  emailSettings: EmailSettings = {
    enabled: true,
    digest: false,
    types: { messages: true, meetings: true, mentions: true },
  };
  emailSettingsLoaded = false;
  emailSettingsSaving = false;
  readonly emailCategories: { key: EmailCategory; label: string; description: string }[] = [
    { key: 'messages', label: 'Direct messages', description: 'Messages sent to you one-on-one.' },
    {
      key: 'meetings',
      label: 'Meeting updates',
      description: 'Requests, confirmations and cancellations.',
    },
    { key: 'mentions', label: 'Mentions & replies', description: 'When someone replies to you.' },
  ];
  hiddenPosts: Post[] = [];
  hiddenLoading = true;
  unhidingIds = new Set<number>();

  /**
   * Sets the browser tab title on init and loads hidden posts and the email notification setting.
   * @returns {void}
   */
  ngOnInit(): void {
    this.title.setTitle('Settings | Handle');
    this.loadHiddenPosts();
    this.loadEmailSettings();
  }

  /**
   * Returns the current authenticated user from UserService.
   * @returns {User | null}
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  /**
   * Fetches the user's email notification settings.
   * @returns {void}
   */
  loadEmailSettings(): void {
    this.emailNotificationsService
      .getSettings()
      .pipe(take(1))
      .subscribe({
        next: (settings) => {
          this.emailSettings = settings;
          this.emailSettingsLoaded = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.emailSettingsLoaded = true;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Saves a change to the email notification settings, reverting to the previous
   * settings if the save fails.
   * @param {EmailSettingsUpdate} update - the settings to change.
   * @returns {void}
   */
  saveEmailSettings(update: EmailSettingsUpdate): void {
    const previous = this.emailSettings;
    this.emailSettings = {
      ...previous,
      ...(update.enabled !== undefined && { enabled: update.enabled }),
      ...(update.digest !== undefined && { digest: update.digest }),
      types: { ...previous.types, ...update.types },
    };
    this.emailSettingsSaving = true;
    this.emailNotificationsService
      .updateSettings(update)
      .pipe(take(1))
      .subscribe({
        next: (settings) => {
          this.emailSettings = settings;
          this.emailSettingsSaving = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.emailSettings = previous;
          this.emailSettingsSaving = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Turns all notification emails on or off.
   * @param {MatSlideToggleChange} event - The toggle change event.
   * @returns {void}
   */
  toggleEmailNotifications(event: MatSlideToggleChange): void {
    this.saveEmailSettings({ enabled: event.checked });
  }

  /**
   * Switches between instant emails and one daily summary email.
   * @param {MatSlideToggleChange} event - The toggle change event.
   * @returns {void}
   */
  toggleEmailDigest(event: MatSlideToggleChange): void {
    this.saveEmailSettings({ digest: event.checked });
  }

  /**
   * Turns emails for one kind of notification on or off.
   * @param {EmailCategory} category - Which kind of notification.
   * @param {MatSlideToggleChange} event - The toggle change event.
   * @returns {void}
   */
  toggleEmailCategory(category: EmailCategory, event: MatSlideToggleChange): void {
    this.saveEmailSettings({ types: { [category]: event.checked } });
  }

  /**
   * Fetches hidden posts from the API.
   * @returns {void}
   */
  loadHiddenPosts(): void {
    this.hiddenLoading = true;
    this.studentPostsService
      .getHiddenPosts()
      .pipe(take(1))
      .subscribe({
        next: (posts) => {
          this.hiddenPosts = posts;
          this.hiddenLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.hiddenLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Unhides a post and removes it from the list.
   * @param {number} postId - The ID of the post to unhide.
   * @returns {void}
   */
  unhidePost(postId: number): void {
    this.unhidingIds.add(postId);
    this.studentPostsService
      .unhidePost(postId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.hiddenPosts = this.hiddenPosts.filter((p) => p.id !== postId);
          this.unhidingIds.delete(postId);
          this.cdr.detectChanges();
        },
        error: () => {
          this.unhidingIds.delete(postId);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Returns a truncated preview of post content.
   * @param {string} content - The full post content.
   * @returns {string}
   */
  truncateContent(content: string): string {
    if (!content) return '';
    return content.length > 120 ? content.substring(0, 120) + '...' : content;
  }
}
